<template>
  <QuickStatsPageShell
    title="Game Previews"
    description="Today’s game preview slate with consensus lines and market activity by league."
    :status-message="statusMessage"
  >
    <template #hero-right>
      <QuickStatsProDashboardBanner />
    </template>

    <QuickStatsLoadingSkeleton v-if="loading" label="Loading game previews" :card-count="2" :row-count="3" />
    <div v-else-if="todayGames.length === 0" class="state-text">
      <strong><em>No free data available at this time. Subscribe in-app for full access.</em></strong>
    </div>

    <section v-else class="league-grid">
      <article v-for="group in groupedGames" :key="group.league" class="card table-card">
        <header class="league-head">
          <div class="league-title-wrap">
            <QuickStatsLeagueMark :league="group.displayLeague" :logo-url="group.leagueLogo" class="league-logo" />
            <h2>{{ group.displayLeague }}</h2>
          </div>
          <p class="updated-at">Updated {{ formatDateTime(group.latestUpdatedAt) }}</p>
        </header>

        <div class="data-table-wrap">
          <table class="data-table preview-table">
            <thead>
              <tr>
                <th>Matchup (Away / Home)</th>
                <th>Consensus Spread (Away / Home)</th>
                <th>Consensus Total (Over / Under)</th>
                <th class="hide-md">Consensus Moneyline (Away / Home)</th>
                <th>Kalshi (Moneyline)</th>
                <th>Polymarket (Moneyline)</th>
                <th aria-label="Detail"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="game in group.games" :key="game.id">
                <td data-label="Matchup">
                  <router-link :to="`/quick-stats/game-previews/${game.gameSlug}`" class="game-link">
                    <div class="matchup-line">
                      <span class="team-token">
                        <img v-if="game.awayTeamLogo" :src="game.awayTeamLogo" :alt="`${game.awayTeam} logo`" class="team-logo" loading="lazy" />
                        <span>{{ game.awayTeam }}</span>
                      </span>
                      <span class="matchup-at">@</span>
                      <span class="team-token">
                        <img v-if="game.homeTeamLogo" :src="game.homeTeamLogo" :alt="`${game.homeTeam} logo`" class="team-logo" loading="lazy" />
                        <span>{{ game.homeTeam }}</span>
                      </span>
                    </div>
                    <span class="matchup-start">{{ formatCompactRoundedDateTime(game.startTime) }}</span>
                  </router-link>
                </td>
                <td data-label="Spread">{{ formatReferenceSpread(game) }}</td>
                <td data-label="Total">{{ formatReferenceTotal(game) }}</td>
                <td class="hide-md" data-label="Moneyline">{{ formatReferenceMoneyline(game) }}</td>
                <td data-label="Kalshi">
                  <span class="venue-chip venue-chip--kalshi">{{ formatVenueChip(game.kalshi) }}</span>
                </td>
                <td data-label="Polymarket">
                  <span class="venue-chip venue-chip--poly">{{ formatVenueChip(game.polymarket) }}</span>
                </td>
                <td data-label="Detail">
                  <router-link :to="`/quick-stats/game-previews/${game.gameSlug}`" class="detail-link">View</router-link>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </article>
    </section>

  </QuickStatsPageShell>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import QuickStatsPageShell from '@/components/QuickStatsPageShell.vue'
import QuickStatsLeagueMark from '@/components/QuickStatsLeagueMark.vue'
import QuickStatsLoadingSkeleton from '@/components/QuickStatsLoadingSkeleton.vue'
import QuickStatsProDashboardBanner from '@/components/QuickStatsProDashboardBanner.vue'
import { averageAmericanOddsPair, formatCompactRoundedDateTime, formatCurrency, formatDateTime, formatLine, oppositeSpread } from '@/lib/quickStats/format'
import { getGamePreviews } from '@/lib/quickStats/service'

const loading = ref(true)
const statusMessage = ref('')
const games = ref([])

const EASTERN_TIME_ZONE = 'America/New_York'

function formatEasternDateKey(value) {
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return ''

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: EASTERN_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  })
    .formatToParts(date)
    .reduce((acc, part) => {
      if (part.type !== 'literal') {
        acc[part.type] = part.value
      }
      return acc
    }, {})

  if (!parts.year || !parts.month || !parts.day) return ''
  return `${parts.year}-${parts.month}-${parts.day}`
}

const todayGames = computed(() => {
  const now = Date.now()
  const todayEasternKey = formatEasternDateKey(new Date())

  return games.value
    .filter((game) => {
      const startDate = new Date(game.startTime)
      if (Number.isNaN(startDate.getTime())) return false
      if (formatEasternDateKey(startDate) !== todayEasternKey) return false
      return startDate.getTime() >= now
    })
})

const groupedGames = computed(() => {
  const grouped = new Map()

  todayGames.value.forEach((game) => {
    if (!grouped.has(game.league)) {
      grouped.set(game.league, {
        league: game.league,
        leagueLogo: game.leagueLogo,
        games: []
      })
    }
    grouped.get(game.league).games.push(game)
  })

  return Array.from(grouped.values())
    .sort((a, b) => a.league.localeCompare(b.league))
    .map((group) => ({
      ...group,
      displayLeague: formatLeagueLabel(group.league),
      latestUpdatedAt: group.games.map((game) => game.updatedAt).filter(Boolean).sort().at(-1) || '',
      games: group.games
        .slice()
        .sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''))
        .slice(0, 3)
    }))
})

function formatReferenceSpread(game) {
  if (game.spread === null) return 'N/A'

  const awaySpread = game.spread
  const homeSpread = oppositeSpread(game.spread)
  const awayLabel = awaySpread === null ? 'N/A' : formatLine(awaySpread)
  const homeLabel = homeSpread === null ? 'N/A' : formatLine(homeSpread)

  return `${awayLabel} / ${homeLabel}`
}

function formatTotalLine(value) {
  if (value === null || value === undefined || value === '') return 'N/A'
  const num = Number(value)
  if (!Number.isFinite(num)) return 'N/A'
  return Number.isInteger(num) ? String(num) : String(Number(num.toFixed(1)))
}

function formatReferenceTotal(game) {
  if (game.total === null) return 'N/A'

  const totalLabel = formatTotalLine(game.total)
  return `O ${totalLabel} / U ${totalLabel}`
}

function formatReferenceMoneyline(game) {
  const prices = averageAmericanOddsPair(game.moneylineBooks, 'awayPrice', 'homePrice')
  if (!prices || !Number.isFinite(prices.first) || !Number.isFinite(prices.second)) return 'N/A'
  return `${formatLine(prices.first)} / ${formatLine(prices.second)}`
}

function formatVenueChip(venue) {
  if (!venue) return 'N/A'
  const volumeLabel = formatCurrency(venue.volumeUsd)
  const hasVolume = volumeLabel !== 'N/A'
  if (!hasVolume) return 'N/A'
  return `Vol ${volumeLabel}`
}

function formatLeagueLabel(value) {
  const label = String(value || '').trim()
  if (!label) return 'N/A'
  return label.toUpperCase()
}

onMounted(async () => {
  const result = await getGamePreviews()
  games.value = result.rows
  statusMessage.value = result.errorMessage
  loading.value = false
})
</script>

<style scoped>
.state-text {
  color: var(--text-muted);
}

.teaser-text {
  margin: 0;
  color: rgba(238, 242, 255, 0.95);
  font-size: 0.9rem;
  line-height: 1.4;
}

.league-grid {
  display: grid;
  gap: 1rem;
  min-width: 0;
}

.table-card {
  padding: 1rem;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
}

.preview-table {
  font-size: 0.78rem;
  min-width: 1020px;
  table-layout: auto;
}

.preview-table th,
.preview-table td {
  padding: 0.5rem 0.56rem;
}

.preview-table th {
  font-size: 0.66rem;
}

.preview-table th:first-child,
.preview-table td:first-child {
  min-width: 420px;
  width: 36%;
  text-align: center;
}

.preview-table th:last-child,
.preview-table td:last-child {
  width: 86px;
  text-align: center;
}

.league-head {
  margin-bottom: 0.7rem;
}

.league-title-wrap {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.league-logo {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.updated-at {
  color: var(--text-muted);
  font-size: 0.82rem;
  margin-top: 0.25rem;
}

.game-link {
  color: var(--text-primary);
  font-weight: 800;
  text-decoration: none;
  display: inline-grid;
  justify-items: center;
  gap: 0.06rem;
  line-height: 1.15;
  margin: 0 auto;
  width: max-content;
  max-width: 100%;
}

.matchup-line {
  display: flex;
  align-items: center;
  gap: 0.38rem;
  flex-wrap: nowrap;
  max-width: 100%;
  white-space: nowrap;
  font-weight: 600;
}

.matchup-at {
  color: var(--text-muted);
  font-size: 0.72rem;
  font-weight: 800;
}

.matchup-start {
  color: var(--text-muted);
  font-size: 0.68rem;
  line-height: 1;
  text-align: center;
}

.team-token {
  display: inline-flex;
  align-items: center;
  gap: 0.26rem;
  white-space: nowrap;
}

.team-logo {
  width: 14px;
  height: 14px;
  flex: 0 0 auto;
  object-fit: contain;
}

.venue-chip {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.16rem 0.46rem;
  border: 1px solid transparent;
  font-size: 0.72rem;
  font-weight: 600;
  white-space: nowrap;
}

.venue-chip--kalshi {
  background: rgba(34, 197, 94, 0.12);
  color: #047857;
  border-color: rgba(34, 197, 94, 0.42);
}

.venue-chip--poly {
  background: rgba(96, 165, 250, 0.13);
  color: #1d4ed8;
  border-color: rgba(59, 130, 246, 0.42);
}

.detail-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 28px;
  border: 1px solid rgba(91, 124, 255, 0.42);
  border-radius: 8px;
  background: rgba(91, 124, 255, 0.1);
  color: var(--accent);
  font-size: 0.74rem;
  font-weight: 800;
  line-height: 1;
  padding: 0.36rem 0.62rem;
  text-decoration: none;
  white-space: nowrap;
}

.detail-link:hover {
  border-color: rgba(91, 124, 255, 0.62);
  background: rgba(91, 124, 255, 0.16);
}

.cross-link {
  margin-top: 1rem;
}

.cross-link a {
  color: var(--accent);
  text-decoration: none;
}

@media (max-width: 700px) {
  .data-table-wrap {
    overflow-x: auto;
    border: 1px solid rgba(202, 220, 255, 0.2);
    border-radius: 8px;
    background: rgba(8, 13, 27, 0.38);
  }

  .preview-table {
    display: table;
    font-size: 0.82rem;
  }

  .preview-table thead {
    display: table-header-group;
  }

  .preview-table tbody {
    display: table-row-group;
  }

  .preview-table tr {
    display: table-row;
    width: auto;
    border: 0;
    border-radius: 0;
    margin: 0;
  }

  .preview-table th,
  .preview-table td {
    display: table-cell;
    width: auto;
    border: 1px solid rgba(202, 220, 255, 0.14);
    padding: 0.58rem 0.7rem;
  }

  .preview-table td::before {
    content: none;
    display: none;
  }
}
</style>
